import { useDrag, useDragLayer, useDrop } from "react-dnd"
import { WorkspaceTreeMaxDepth } from "../constants"
import { ID } from "@nova/constants"
import { findMaxTreeDepth, findTagAncestorIds, flattenTagTree } from "../module"
import {
    WorkspaceTreeItem,
    selectLegacyWorkspaceTree,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import { selectProjectsByTagFilter } from "features/entity-caching/fires/selectors"
import { useMemo } from "react"
import { WorkspaceEntityType } from "../types"

type UseWorkspaceFolderDragAndDropProps = {
    id: ID
    subFolders?: WorkspaceTreeItem[]
    isEditor: boolean
    isRenaming: boolean
    handleMove: (id: ID, type: WorkspaceEntityType, newParentFolderId?: ID) => void
    extraCanDropCondition?: ({ id, type }: { id: ID; type: WorkspaceEntityType }) => boolean
}

export const useWorkspaceFolderDragAndDrop = ({
    id,
    subFolders,
    isEditor,
    isRenaming,
    handleMove,
    extraCanDropCondition,
}: UseWorkspaceFolderDragAndDropProps) => {
    const sidebarTree = useAppSelector(selectLegacyWorkspaceTree)
    const tagChain = (sidebarTree && findTagAncestorIds(sidebarTree, id)) || []
    const projects = useAppSelector(selectProjectsByTagFilter(tagChain))

    // Immediate children only.
    const projectIdsInThisFolder = useMemo(() => {
        const set = new Set<ID>()
        projects.forEach((project) => set.add(project.id))
        return set
    }, [projects])

    // Immediate children only.
    const subFolderIdsInThisFolder = useMemo(
        () => new Set(subFolders?.map((subFolder) => subFolder.id)),
        [subFolders]
    )

    const [{ isDragging }, dragRef] = useDrag(() => {
        const flattenedChildren: Record<ID, WorkspaceTreeItem> = {}
        if (subFolders) flattenTagTree(subFolders, flattenedChildren)

        return {
            type: WorkspaceEntityType.Folder,
            item: {
                id,
                type: WorkspaceEntityType.Folder,
                depthOfChildren: findMaxTreeDepth({ subTags: subFolders || [] }),
                children: new Set(Object.keys(flattenedChildren)),
            },
            collect: (monitor) => ({
                isDragging: !!monitor.isDragging(),
            }),
            canDrag: !isRenaming && isEditor,
        }
    }, [isRenaming, isEditor, id, subFolders])

    const [{ isOver, canDrop }, dropRef] = useDrop(
        () => ({
            accept: [WorkspaceEntityType.Project, WorkspaceEntityType.Folder],
            drop: (item: {
                id: string
                type: WorkspaceEntityType
                depthOfChildren?: number
                children?: Set<ID>
            }) => {
                if (item.type === WorkspaceEntityType.Project) {
                    handleMove(item.id, WorkspaceEntityType.Project, id)
                } else if (item.type === WorkspaceEntityType.Folder) {
                    handleMove(item.id, WorkspaceEntityType.Folder, id)
                }
            },
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
                canDrop: !!monitor.canDrop(),
            }),
            canDrop: (item) =>
                ((item) => {
                    if (item.type === WorkspaceEntityType.Project) {
                        return !projectIdsInThisFolder.has(item.id)
                    } else if (
                        item.type === WorkspaceEntityType.Folder &&
                        item.depthOfChildren !== undefined &&
                        item.children
                    ) {
                        // If dropRef is child of dragRef (will make cycle)...
                        if (item.children.has(id) || id === item.id) return false

                        // If dropRef is already an immediate child of dragRef...
                        // IMPORTANT - Allowing this will cause the object to get
                        // deleted after the pointless move operation in handleMoveFolder.
                        if (subFolderIdsInThisFolder.has(item.id)) return false

                        // If the new chain of folders will go too deep...
                        const depthOfSelf = tagChain.length
                        if (item.depthOfChildren + depthOfSelf > WorkspaceTreeMaxDepth) return false

                        return true
                    }
                    return false
                })(item) && (extraCanDropCondition ? extraCanDropCondition(item) : true),
        }),
        [id, projectIdsInThisFolder, tagChain, subFolderIdsInThisFolder, extraCanDropCondition]
    )

    const anythingIsDragging = useDragLayer((monitor) => {
        const type = monitor.getItemType()
        const isAcceptableType =
            type === WorkspaceEntityType.Folder || type === WorkspaceEntityType.Project
        return monitor.isDragging() && isAcceptableType
    })

    const dragAndDropRef = (div: HTMLDivElement) => {
        dragRef(div)
        dropRef(div)
    }

    return {
        dragAndDropRef,
        dragRef,
        dropRef,
        isOver,
        canDrop,
        isDragging,
        anythingIsDragging,
    }
}
